import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { css } from '@mui/material/styles';
import { type TextFieldProps } from '@mui/material/TextField';
import { useInputStyles } from './input.styles';
import { type ChangeEvent, type JSX } from 'react';
import { useIntl } from '@doxyme/locale';
import { IconButton } from '../../iconButton';
import { Status } from '../../status';
import { Icon } from '@doxyme/icons';

export type InputProps = {
  size?: 'regular' | 'small';
  isComplete?: boolean;
  /**
   * Hides the clear icon if true.
   */
  isActive?: boolean;
  hasError?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  /**
   * If true prevent the user from making any changes, remove icons or labels
   * from the field and apply dimming overlay to any text content.
   * @default false
   */
  disabled?: boolean;
  /**
   * If false, the text content cannot be modified by the user.
   * Also removes any annotations to the Input field.
   * @default true
   */
  maxLength?: number;
  value?: string;
  contentEditable?: boolean;
  placeholder?: string;
  startAdornment?: JSX.Element | null;
  endAdornment?: JSX.Element | null;
  decorativeIcon?: JSX.Element;
  testId?: string;
  isClearIconShown?: boolean;
  tabIndex?: number;
} & Omit<
  TextFieldProps,
  'size' | 'onChange' | 'error' | 'helperText' | 'label' | 'variant'
>;

export const Input = ({
  size = 'small',
  isComplete = false,
  isActive = false,
  hasError = false,
  value,
  maxLength,
  onChange,
  disabled = false,
  contentEditable = true,
  placeholder,
  decorativeIcon,
  startAdornment,
  endAdornment,
  type,
  testId,
  isClearIconShown = false,
  tabIndex,
  ...muiProps
}: InputProps): JSX.Element => {
  const handleClearInput = (): void => {
    onChange?.({
      target: { value: '' },
    } as ChangeEvent<HTMLInputElement>);
  };

  const isFilled = Boolean(value?.length);

  const styles = useInputStyles({
    isFilled,
    hasError,
    size,
    disabled,
    contentEditable,
  });

  const { formatMessage } = useIntl();

  const canEdit = Boolean(!disabled && contentEditable);
  const getStateBasedEndAdornments = (): JSX.Element | null => {
    switch (true) {
      case isActive: {
        return null;
      }

      case isComplete: {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mr: 1,
            }}
          >
            <Status statusLevel='success' size={20} />
          </Box>
        );
      }

      case hasError: {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mr: 1,
            }}
          >
            <Status statusLevel='error' size={20} />
          </Box>
        );
      }

      case isClearIconShown && isFilled && canEdit: {
        return (
          <IconButton
            icon={<Icon.Cross type='filled' size='small' />}
            type='subtle'
            size='medium'
            onClick={handleClearInput}
            title={formatMessage({ id: 'textFieldClearTooltip' })}
          />
        );
      }

      default: {
        return null;
      }
    }
  };

  return (
    <TextField
      {...muiProps}
      type={type}
      placeholder={placeholder}
      disabled={!canEdit}
      error={hasError}
      css={css(styles.muiTextFieldCSS, muiProps.className)}
      onChange={onChange}
      value={value}
      inputProps={{ maxLength, tabIndex, ...muiProps.inputProps }}
      InputProps={{
        startAdornment,
        endAdornment: (
          <>
            <>{decorativeIcon}</>
            <>{getStateBasedEndAdornments()}</>
            {Boolean(endAdornment) && (
              <Box css={styles.endAdornmentIconWrapperCSS}>{endAdornment}</Box>
            )}
          </>
        ),
        ...(testId !== undefined ? { 'data-testid': testId } : undefined),
        ...muiProps.InputProps,
      }}
    />
  );
};
